<template>
    <div class="container-fluid">
        <div class="card">
            <div class="card-body">

                <!-- Global Override Alert -->
                <div v-if="globalAiCategorySetting !== 0"
                    class="global-alert"
                    :class="{ 'alert-allow': globalAiCategorySetting === 1, 'alert-block': globalAiCategorySetting === 2 }">
                    <div class="d-flex align-items-center">
                        <i class="fa-solid fa-circle-info me-2" style="font-size: 18px;"></i>
                        <div>
                            <strong>{{ globalOverrideMessage }}</strong>
                            <div class="mt-1" style="font-size: 13px; opacity: 0.85;">
                                To manage individual AI categories, set "AI Categories" to "Off" in
                                <a href="/account-settings" class="alert-link">Configuration <i class="fa-solid fa-arrow-right mx-1"></i> Settings</a>.
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Search + Counter -->
                <div class="row mb-3">
                    <div class="col-12 col-md-4">
                        <input v-model="searchQuery" type="text" class="form-control form-control-sm"
                            placeholder="&#xF002; Search AI platforms..."
                            style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;">
                    </div>
                    <div class="col-12 col-md-8 d-flex align-items-center justify-content-end">
                        <small class="text-muted">{{ filteredCategories.length }} of {{ masterAiCategoryList.length }} AI platforms</small>
                    </div>
                </div>

                <!-- Loading -->
                <div v-if="isLoading" class="d-flex justify-content-center my-5">
                    <div class="spinner-border" style="color: #00a0c1; width: 2.5rem; height: 2.5rem;" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>

                <!-- AI Categories Grid -->
                <div v-else class="row">
                    <div class="col-xl-6 col-lg-6 col-md-12 mb-2" v-for="cat in filteredCategories" :key="cat.Domain">
                        <div class="ai-row">
                            <div class="ai-label">
                                <span class="ai-name">{{ cat.Name }}</span>
                                <span class="ai-desc">{{ cat.Domain }} — {{ cat.Description }}</span>
                            </div>
                            <div class="btn-group" role="group">
                                <button type="button"
                                    class="btn btn-outline-left btn-sm rounded-start-pill btn-check-left hand-cursor"
                                    @click="handleSingleCategoryChange(cat.Domain, 1)"
                                    :class="{ active: isAllowActive(cat) }"
                                    :disabled="areButtonsDisabled || action_cooldowns[cat.Domain]">Allow</button>

                                <button type="button"
                                    class="btn btn-outline-center btn-sm btn-check-center hand-cursor"
                                    @click="handleSingleCategoryChange(cat.Domain, 0)"
                                    :class="{ active: isOffActive(cat) }"
                                    :disabled="areButtonsDisabled || action_cooldowns[cat.Domain]">Off</button>

                                <button type="button"
                                    class="btn btn-outline-right btn-sm rounded-end-pill btn-check-right hand-cursor"
                                    @click="handleSingleCategoryChange(cat.Domain, 2)"
                                    :class="{ active: isBlockActive(cat) }"
                                    :disabled="areButtonsDisabled || action_cooldowns[cat.Domain]">Block</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="!isLoading && filteredCategories.length === 0" class="text-center text-muted py-4">
                    No AI platforms match your search.
                </div>

            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            isLoading: true,
            isSaving: false,
            masterAiCategoryList: [],
            categorySettings: {},
            globalAiCategorySetting: 0,
            searchQuery: '',
            action_cooldowns: {},
        };
    },
    computed: {
        areButtonsDisabled() {
            return this.isSaving || this.globalAiCategorySetting !== 0;
        },
        globalOverrideMessage() {
            if (this.globalAiCategorySetting === 1) {
                return "Global Setting Active: All AI Categories are set to ALLOW.";
            }
            if (this.globalAiCategorySetting === 2) {
                return "Global Setting Active: All AI Categories are set to BLOCK.";
            }
            return "";
        },
        filteredCategories() {
            if (!this.searchQuery) return this.masterAiCategoryList;
            const q = this.searchQuery.toLowerCase();
            return this.masterAiCategoryList.filter(cat =>
                cat.Name.toLowerCase().includes(q) ||
                cat.Domain.toLowerCase().includes(q) ||
                (cat.Description && cat.Description.toLowerCase().includes(q))
            );
        }
    },
    mounted() {
        this.fetchSettings();
    },
    methods: {
        isAllowActive(cat) {
            return this.globalAiCategorySetting === 1 || (this.globalAiCategorySetting === 0 && this.categorySettings[cat.Domain] === 1);
        },
        isOffActive(cat) {
            return this.globalAiCategorySetting === 0 && (this.categorySettings[cat.Domain] === undefined || this.categorySettings[cat.Domain] === null || this.categorySettings[cat.Domain] === 0);
        },
        isBlockActive(cat) {
            return this.globalAiCategorySetting === 2 || (this.globalAiCategorySetting === 0 && this.categorySettings[cat.Domain] === 2);
        },

        setCooldown(key) {
            if (typeof this.$set === 'function') {
                this.$set(this.action_cooldowns, key, true);
                setTimeout(() => {
                    this.$delete(this.action_cooldowns, key);
                }, 1000);
            } else {
                this.action_cooldowns[key] = true;
                setTimeout(() => {
                    delete this.action_cooldowns[key];
                }, 1000);
            }
        },

        async fetchSettings() {
            this.isLoading = true;
            try {
                const response = await this.$axios.get("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/get-master-ai-categories");

                const allCategories = response.data.all_ai_categories || [];
                const userCategorySettings = response.data.ai_category_settings || [];
                const accountSettings = response.data.account_settings || {};

                this.globalAiCategorySetting = accountSettings.ai_category_setting || 0;
                this.masterAiCategoryList = allCategories;

                const userSettingsMap = userCategorySettings.reduce((acc, setting) => {
                    acc[setting.domain] = setting.enabled_type;
                    return acc;
                }, {});

                const settings = {};
                for (const cat of allCategories) {
                    settings[cat.Domain] = userSettingsMap[cat.Domain] || 0;
                }
                this.categorySettings = settings;

            } catch (error) {
                this.$toast.error(error.response?.data?.message || "Could not load AI category settings.");
            } finally {
                this.isLoading = false;
            }
        },

        async handleSingleCategoryChange(domain, value) {
            if (this.areButtonsDisabled) return;
            if (this.action_cooldowns[domain]) return;
            if (this.categorySettings[domain] === value) return;

            this.setCooldown(domain);

            const oldValue = this.categorySettings[domain];
            this.categorySettings[domain] = value;
            this.isSaving = true;

            try {
                await this.$axios.post("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/update-ai-category-status", {
                    domain: domain,
                    enabled_type: value,
                });
                this.$toast.success(`Updated AI category settings for ${domain}. Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.`);
            } catch (error) {
                this.categorySettings[domain] = oldValue;
                this.$toast.error(error.response?.data?.message || `Failed to update ${domain}.`);
            } finally {
                this.isSaving = false;
            }
        },

        async handleBulkChange(value) {
            this.isSaving = true;
            const originalSettings = { ...this.categorySettings };

            for (const domain in this.categorySettings) {
                this.categorySettings[domain] = value;
            }

            try {
                await this.$axios.post("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/sync-all-ai-categories-status", {
                    enabled_type: value,
                });
                this.$toast.success("All AI category settings updated successfully! Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.");
            } catch (error) {
                this.categorySettings = originalSettings;
                this.$toast.error(error.response?.data?.message || "Failed to update all settings.");
            } finally {
                this.isSaving = false;
            }
        },
    },
};
</script>

<style scoped>
.card {
    border: none;
}

/* Global alert */
.global-alert {
    padding: 14px 18px;
    border-radius: 10px;
    margin-bottom: 16px;
    font-size: 14px;
}

.global-alert.alert-allow {
    background-color: #e6f9fc;
    color: #006073;
    border: 1px solid #b8e8f0;
}

.global-alert.alert-block {
    background-color: #fef5ed;
    color: #9a6231;
    border: 1px solid #fbe0c4;
}

.global-alert .alert-link {
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
}

/* AI row layout */
.ai-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border-radius: 8px;
    transition: background-color 0.15s ease;
}

.ai-row:hover {
    background-color: #f8f9fa;
}

.ai-label {
    display: flex;
    flex-direction: column;
    flex: 1;
    margin-right: 1rem;
}

.ai-name {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
}

.ai-desc {
    font-size: 12px;
    color: #6c757d;
    margin-top: 1px;
}

/* Button group styles */
.btn-check-left { min-width: 5rem; max-height: 2.2rem; border-color: gray; }
.btn-check-center { min-width: 3.5rem; max-height: 2.2rem; border-color: gray; }
.btn-check-right { min-width: 5rem; max-height: 2.2rem; border-color: gray; }

.btn-outline-right { color: #f3994d; background-color: transparent; border-color: grey }
.btn-outline-right:hover, .btn-outline-right.active, .btn-outline-right:active { color: whitesmoke; background-color: #f3994d; border-color: #f3994d; font-weight: bold;}

.btn-outline-center { color: #a3c0d1; background-color: transparent; border-color: grey }
.btn-outline-center:hover, .btn-outline-center.active, .btn-outline-center:active { color: whitesmoke; background-color: #a3c0d1; border-color: #a3c0d1; font-weight: bold;}

.btn-outline-left { color: #00a0c1; background-color: transparent; border-color: grey }
.btn-outline-left:hover, .btn-outline-left.active, .btn-outline-left:active { color: whitesmoke; background-color: #00a0c1; border-color: #00a0c1; font-weight: bold;}

.hand-cursor {
    cursor: pointer;
}
</style>
